"use client";

import { Instagram, Linkedin, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function FooterNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="flex flex-col gap-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i} className="h-5 w-32 bg-muted/40 animate-pulse rounded-md" />)}
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="group/nav">
          <Link to={item.href || "#"} aria-label={item.label} className="text-foreground inline-flex items-center text-[0.9375rem] font-default transition-all duration-240 hover:text-primary">
            <span aria-hidden="true" className="mr-0 inline-block h-1.5 w-1.5 rounded-lg bg-primary opacity-0 -translate-x-2 transition-all duration-240 group-hover/nav:opacity-100 group-hover/nav:translate-x-0 group-hover/nav:mr-3" />
            <span className="transition-all duration-240">{item.label}</span>
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="1630" id="footer" className="bg-background text-foreground relative font-default">
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-px" style={{
      background: "linear-gradient(to right, hsla(43, 50%, 55%, 0) 0%, hsla(43, 50%, 55%, 0.7) 50%, hsla(43, 50%, 55%, 0) 100%)"
    }} />

      <div ref={ref} className={`mx-auto max-w-[1440px] px-6 md:px-16 py-24 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5 flex flex-col gap-6">
            <Link to="/" aria-label="Orvo Web — Home" className="inline-block">
              <WvcLogo className="h-9 w-auto" />
            </Link>

            <p className="text-[1rem] leading-[1.65] font-default text-muted-foreground max-w-md">
              Orvo Web &middot; Webdesign Agentur &middot; Oldenburg, Deutschland
            </p>

            <p className="font-mono text-[0.75rem] uppercase text-muted-foreground" style={{
            letterSpacing: "0.14em",
            lineHeight: 1.4
          }}>
              &middot; Digitales Handwerk &middot; Premium Lösungen &middot; Oldenburg
            </p>

            <div aria-hidden="true" className="h-px w-60 mt-2" style={{
            background: "linear-gradient(to right, hsla(43, 50%, 55%, 0.8) 0%, hsla(43, 50%, 55%, 0) 100%)"
          }} />
          </div>

          <div className="md:col-span-3 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 01 / NAVIGATION
            </h3>
            <MenuProvider menu_id="17">
              <FooterNavConsumer />
            </MenuProvider>
          </div>

          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 02 / SOCIALS
            </h3>
            <div className="flex items-center gap-5">
              <Link to="#" aria-label="Orvo Web auf Instagram" className="text-foreground transition-colors duration-240 hover:text-primary">
                <Instagram strokeWidth={1.25} className="h-6 w-6" />
              </Link>
              <Link to="#" aria-label="Orvo Web auf LinkedIn" className="text-foreground transition-colors duration-240 hover:text-primary">
                <Linkedin strokeWidth={1.25} className="h-6 w-6" />
              </Link>
              <Link to="https://g.page/r/orvoweb" newTab aria-label="Orvo Web auf Google Business" className="text-foreground transition-colors duration-240 hover:text-primary">
                <Search strokeWidth={1.25} className="h-6 w-6" />
              </Link>
            </div>
          </div>

          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 03 / IMPRESSUM
            </h3>
            <div className="flex flex-col gap-2">
              <p className="text-foreground text-[0.9375rem] font-default">
                Jerome Werner
              </p>
              <p className="text-muted-foreground text-[0.875rem] leading-relaxed">
                Parkstraße 28,<br />
                27798 Hude
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <p className="text-muted-foreground text-[0.75rem] font-mono uppercase" style={{
                letterSpacing: "0.1em"
              }}><br /></p>
                <p className="text-muted-foreground text-[0.75rem] font-mono uppercase" style={{
                letterSpacing: "0.1em"
              }}><br /></p>
                <Link to="mailto:Jerome@orvoweb.de" className="font-mono text-muted-foreground text-[0.8125rem] uppercase transition-colors duration-240 hover:text-primary break-all" style={{
                letterSpacing: "0.14em",
                lineHeight: 1.4
              }}>
                  JEROME@ORVOWEB.DE
                </Link>
                <Link to="tel:017641368728" className="font-mono text-muted-foreground text-[0.8125rem] uppercase transition-colors duration-240 hover:text-primary" style={{
                letterSpacing: "0.14em",
                lineHeight: 1.4
              }}>
                  017641368728
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div aria-hidden="true" className="mt-20 h-px w-full" style={{
        background: "linear-gradient(to right, hsla(43, 50%, 55%, 0) 0%, hsla(43, 50%, 55%, 0.5) 50%, hsla(43, 50%, 55%, 0) 100%)"
      }} />

        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-mono text-muted-foreground text-[0.6875rem] uppercase" style={{
          letterSpacing: "0.18em",
          lineHeight: 1
        }}>© 2026 ORVO WEB · ALLE RECHTE VORBEHALTEN</p>
          <p className="font-mono text-muted-foreground text-[0.6875rem] uppercase inline-flex items-center gap-2" style={{
          letterSpacing: "0.18em",
          lineHeight: 1
        }}>
            <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-lg bg-primary" />
            DESIGNED IN OLDENBURG
          </p>
        </div>
      </div>
    </section>;
}